/**
 * TCR-1517 (Plan 3, Task 3) — thành phần của một COMBO THẬT.
 *
 * Combo thật LÀ một sản phẩm: có SKU, biến thể và tồn kho riêng, nên nó chiếm
 * ĐÚNG MỘT dòng đơn hàng như mọi sản phẩm khác. Danh sách dưới đây KHÔNG phải
 * dòng đơn hàng — nó là snapshot định mức (BOM) chụp lại lúc bán và lưu trên
 * chính dòng đó (`order.items[].bom_components`), chỉ để người xem biết bên
 * trong combo có gì. Vì vậy màn hình nào cũng render nó ở chế độ CHỈ XEM:
 * không nút, không input, không cột giá (combo chỉ có MỘT mức giá, nằm ở dòng
 * cha) — không có gì để tách ra thành dòng riêng.
 *
 * ## Vì sao KHÔNG còn là "một cái bảng dùng chung"
 *
 * Bản cũ render một `<table>` riêng có lưới cột RIÊNG, lồng bên dưới dòng cha.
 * Hệ quả: cột "Số lượng" của nó không bao giờ thẳng hàng với cột SL của bảng
 * đơn hàng ở trên — người xem đọc hai con số cùng nghĩa ở hai vị trí khác nhau.
 * Muốn thẳng hàng thì hàng thành phần phải dùng ĐÚNG lưới cột của bảng cha, mà
 * mỗi màn có một lưới khác nhau:
 *
 * - chi tiết đơn: `<table>` + `<colgroup>` 8 cột ⇒ mỗi thành phần là một `<tr>`;
 * - giỏ POS / sửa đơn: flexbox theo `comboGroupColumns()` ⇒ mỗi thành phần là
 *   một dòng flex dựng từ chính danh sách cột đó;
 * - pop-up xem nhanh đơn: không có cột nào ⇒ vài dòng chữ như combo ảo.
 *
 * Nên phần dùng chung rút về đúng thứ KHÔNG phụ thuộc lưới cột: ô sản phẩm
 * (ảnh · tên · SKU) và cách tính/format số lượng. Mỗi màn tự đặt hai thứ đó vào
 * ô cột của mình. Đừng gom ngược lại thành một bảng "dùng chung" nữa — cái giá
 * của nó là lệch cột ở cả ba màn.
 */
import { POS_IMAGE_FALLBACK } from '@/pages/POS/utils/imagePlaceholder';

/** Một thành phần trong định mức combo. Tên field giữ nguyên snake_case đúng
 *  như payload API (ADR-013: field JSON snake_case, không mapping casing). */
export interface ComboComponent {
  sku: string;
  product_name: string;
  unit_name: string;
  /** Số lượng thành phần này trong MỘT combo. */
  qty_per_unit: number;
  /**
   * Ảnh sản phẩm thành phần. Đơn bán TRƯỚC khi BE chụp ảnh vào snapshot không
   * có field này ⇒ ô ảnh hiện placeholder xám, KHÔNG được vì thế mà giấu dòng.
   */
  image?: string;
}

/** Bỏ đuôi `.0` cho số tròn (định mức có thể lẻ, vd 0,5 kg vàng). */
export function formatComboQty(value: number): string {
  return Number(value.toFixed(3)).toLocaleString('vi-VN');
}

/**
 * Số combo trong dòng, đã kẹp về ngưỡng hợp lệ.
 *
 * `?? 1` chứ không phải `|| 1`: số combo hợp lệ luôn ≥ 1, nhưng dữ liệu hỏng
 * (0/âm) không được biến thành 1 một cách im lặng — kẹp về 1 và nói đúng định
 * mức cho MỘT combo thay vì nhân ra số âm vô nghĩa.
 */
export function comboMultiplier(multiplier?: number): number {
  return multiplier == null || multiplier < 1 ? 1 : multiplier;
}

/** Số lượng thành phần sẽ hiện ở cột SL: định mức × số combo trong dòng. */
export function comboComponentQty(component: ComboComponent, multiplier?: number): string {
  return formatComboQty(component.qty_per_unit * comboMultiplier(multiplier));
}

export interface ComboComponentCellProps {
  component: ComboComponent;
  /** Thụt lề trái để dòng thành phần nhìn ra là con của dòng combo phía trên. */
  indent?: number;
  /** Bề rộng/chiều cao ô ảnh. Mặc định 40 — cùng cỡ ảnh dòng thường. */
  imageSize?: number;
}

/**
 * Ô sản phẩm của một dòng thành phần: ảnh · tên · SKU — cùng cách trình bày với
 * dòng thành phần của combo ẢO, để hai loại combo không đọc như hai hệ thống
 * khác nhau. KHÔNG có đơn vị tính ở đây: cột ĐVT đã bị bỏ khỏi danh sách thành
 * phần (nó chỉ có nghĩa ở màn cấu hình định mức / lệnh lắp ráp, nơi người dùng
 * đang cân đong hàng thật).
 *
 * Dùng `<img>` trần + `onError` chứ không dùng antd `<Image>`: ô này lặp lại
 * rất nhiều lần trong một màn (mỗi combo vài dòng), và không cần preview.
 */
export function ComboComponentCell({
  component,
  indent = 0,
  imageSize = 40,
}: ComboComponentCellProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, paddingLeft: indent }}>
      <img
        src={component.image || POS_IMAGE_FALLBACK}
        alt={component.product_name}
        onError={(event) => {
          event.currentTarget.src = POS_IMAGE_FALLBACK;
        }}
        style={{
          width: imageSize,
          height: imageSize,
          flex: 'none',
          objectFit: 'cover',
          borderRadius: 4,
          background: '#f5f5f5',
          border: '1px solid #f0f0f0',
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#333', overflowWrap: 'anywhere' }}>
          {component.product_name || component.sku}
        </div>
        <div style={{ marginTop: 2, fontSize: 11, color: '#6b6b6b', overflowWrap: 'anywhere' }}>
          {component.sku}
        </div>
      </div>
    </div>
  );
}

export interface ComboComponentsPlainListProps {
  components: ComboComponent[];
  /** Số combo trong dòng — dùng để hiện tổng số lượng thực tế. */
  multiplier?: number;
  /** Tiền tố `data-testid`, định danh DÒNG đang bung (vd `pos-item-i1-`). */
  idPrefix?: string;
}

/**
 * Danh sách thành phần cho nơi KHÔNG có lưới cột để bám vào (bảng đã ngừng
 * mount ở `pages/Orders/components/OrderItemsTable.tsx`). Số lượng đứng ngay
 * sau ô sản phẩm thay vì cố giả vờ thẳng hàng với một cột không tồn tại.
 *
 * Màn thật (chi tiết đơn, giỏ POS, sửa đơn) KHÔNG dùng hàm này — chúng tự dựng
 * hàng theo lưới cột của mình, xem doc comment đầu file.
 */
export function ComboComponentsPlainList({
  components,
  multiplier,
  idPrefix = '',
}: ComboComponentsPlainListProps): JSX.Element | null {
  // Đơn CŨ bán trước khi BE chụp snapshot không có `bom_components` — người gọi
  // truyền `[]` và ở đây không render gì, không nút mở rộng, không vỡ.
  if (components.length === 0) return null;

  return (
    <div data-testid={`${idPrefix}combo-components-list`} style={{ padding: '4px 0' }}>
      {components.map((component, index) => (
        <div
          key={`${component.sku}-${index}`}
          data-testid={`${idPrefix}combo-component-row-${component.sku}`}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '2px 0' }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <ComboComponentCell component={component} imageSize={32} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#595959' }}>
            {comboComponentQty(component, multiplier)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default ComboComponentCell;
